 
/**
 * Entity Service
 *
 * Service for interacting with the accounts-api entity management system.
 * Entities represent businesses, individuals, or organizations.
 */

import { buildQueryString, type ApiResponse, type PageInfo } from './api-client'

// Entity service is proxied through the accounting server to avoid CORS issues
/* v8 ignore next */
const API_BASE_URL = import.meta.env.VITE_API_URL ?? 'http://localhost:11002'

// Helper function to call entity endpoints through the accounting API gateway
async function fetchEntity<T>(
  path: string,
  options?: RequestInit
): Promise<ApiResponse<T>> {
  try {
    const response = await fetch(`${API_BASE_URL}${path}`, {
      ...options,
      headers: {
        'Content-Type': 'application/json',
        ...options?.headers,
      },
    })

    if (!response.ok) {
      const errorBody = await response.json().catch(() => ({ message: response.statusText })) as { message?: string }
      return {
        error: {
          code: `HTTP_${response.status}`,
          message: errorBody.message ?? `Request failed with status ${response.status}`,
        },
      }
    }

    const data = await response.json() as T
    return { data }
  } catch (error: unknown) {
    return {
      error: {
        code: 'NETWORK_ERROR',
        message: error instanceof Error ? error.message : 'Network request failed',
      },
    }
  }
}

// Wrapper functions that match the api-client interface
function get<T>(path: string): Promise<ApiResponse<T>> {
  return fetchEntity<T>(path, { method: 'GET' })
}

function post<T>(path: string, data?: unknown): Promise<ApiResponse<T>> {
  return fetchEntity<T>(path, {
    method: 'POST',
    /* v8 ignore next */
    body: data ? JSON.stringify(data) : undefined,
  })
}

function patch<T>(path: string, data?: unknown): Promise<ApiResponse<T>> {
  return fetchEntity<T>(path, {
    method: 'PATCH',
    /* v8 ignore next */
    body: data ? JSON.stringify(data) : undefined,
  })
}

function del<T>(path: string): Promise<ApiResponse<T>> {
  return fetchEntity<T>(path, { method: 'DELETE' })
}

// Entity Types
export interface BusinessIdentifier {
  type: string          // 'gstin' | 'pan' | 'tan' | 'cin' | 'udyam' | 'vat' | 'ein' | 'ssn' | etc.
  value: string
  verified?: boolean
}

export interface EntityAddress {
  id?: string            // MongoDB ObjectId as string (toJSON transforms _id to id)
  label: string          // 'billing' | 'shipping' | 'registered' | 'warehouse' | etc.
  type: string           // 'office' | 'residential' | 'warehouse' | etc.
  usage?: string[]       // How this address is used: ['billing', 'shipping', 'consignee']
  line1: string          // Address line 1
  line2?: string         // Address line 2 (optional)
  city: string
  state?: string         // Optional - not all countries have states
  stateCode?: string     // e.g., "07" for Delhi, "29" for Karnataka
  postalCode: string     // Changed from pincode to match camelCase standard
  country: string
  countryCode?: string   // e.g., "IN" for India, "US" for USA
  gstin?: string         // State-specific GSTIN for this address
  companyCode?: string   // Customer's internal company code (camelCase)
  isPrimary?: boolean
}

export interface EntityBankAccount {
  id?: string            // UUID
  accountName: string
  nameAtBank?: string
  accountType?: string   // 'current' | 'savings' | 'od' | 'cc' | 'fd' | 'cash'
  bankName?: string
  branch?: string
  accountNumber?: string
  ifscCode?: string      // India
  swiftCode?: string     // International
  iban?: string          // International
  routingNumber?: string // USA
  sortCode?: string      // UK
  currency?: string
  isDefault?: boolean
  isActive?: boolean
  notes?: string
}

export interface Entity {
  id: string  // MongoDB ObjectId as string (toJSON transforms _id to id)
  name: string
  website?: string
  logo?: string          // Company logo (URL or base64 data URI) — shared across apps
  businessIdentifiers?: BusinessIdentifier[]
  contactHandles?: ContactHandle[]
  contactIds?: string[]
  tags?: string[]
  addresses?: EntityAddress[]
  bankAccounts?: EntityBankAccount[]
  mergedIds?: string[]
  status: string         // 'active' | 'inactive' | 'archived'
  createdAt: string
  updatedAt: string
}

// Request/Response Types
export interface ContactHandle {
  type: string       // 'email' | 'phone' | 'mobile' | 'website'
  value: string
  is_primary?: boolean
}

export interface CreateEntityRequest {
  name: string
  tradeName?: string
  website?: string
  logo?: string              // Company logo (URL or base64 data URI)
  countryCode: string        // Primary country code for the entity (required)
  businessIdentifiers?: BusinessIdentifier[]
  contactHandles?: ContactHandle[]  // Contact handles (email, phone, etc.)
  contactIds?: string[]
  tags?: string[]
  addresses?: EntityAddress[]
  bankAccounts?: EntityBankAccount[]
  status?: string
}

export interface UpdateEntityRequest {
  name?: string
  tradeName?: string
  website?: string
  logo?: string
  countryCode?: string
  businessIdentifiers?: BusinessIdentifier[]
  contactHandles?: ContactHandle[]  // Contact handles (email, phone, etc.)
  contactIds?: string[]
  tags?: string[]
  addresses?: EntityAddress[]
  bankAccounts?: EntityBankAccount[]
  status?: string
}

export interface ListEntitiesFilter {
  status?: string
  tags?: string[]
  query?: string         // Search by name
}

export interface ListEntitiesResponse {
  entities: Entity[]
  pageInfo?: PageInfo
}

/**
 * Create a new entity
 */
export async function createEntity(
  data: CreateEntityRequest
): Promise<ApiResponse<Entity>> {
  // Send camelCase - backend DTOs now use camelCase
  const requestData = {
    name: data.name,
    countryCode: data.countryCode,
    tradeName: data.tradeName,
    website: data.website,
    logo: data.logo,
    businessIdentifiers: data.businessIdentifiers,
    contactHandles: data.contactHandles,
    contactUuids: data.contactIds,
    tags: data.tags,
    addresses: data.addresses?.map(addr => ({
      id: addr.id,
      label: addr.label,
      type: addr.type,
      line1: addr.line1,
      line2: addr.line2,
      city: addr.city,
      state: addr.state,
      stateCode: addr.stateCode,
      postalCode: addr.postalCode,
      country: addr.country,
      countryCode: addr.countryCode,
      gstin: addr.gstin,
      companyCode: addr.companyCode,
      isPrimary: addr.isPrimary,
    })),
    bankAccounts: data.bankAccounts,
    status: data.status,
  }

  return post<Entity>('/entities', requestData)
}

/**
 * Get entity by ID
 */
export async function getEntity(
  entityId: string
): Promise<ApiResponse<Entity>> {
  return get<Entity>(`/entities/${entityId}`)
}

/**
 * Build the update payload from UpdateEntityRequest, copying only defined fields.
 * Extracted to reduce complexity in updateEntity.
 */
function buildUpdatePayload(data: UpdateEntityRequest): Record<string, unknown> {
  const requestData: Record<string, unknown> = {}

  // Fields that map directly (source key -> target key)
  const SIMPLE_FIELDS: Array<[keyof UpdateEntityRequest, string]> = [
    ['name', 'name'],
    ['tradeName', 'tradeName'],
    ['website', 'website'],
    ['logo', 'logo'],
    ['countryCode', 'countryCode'],
    ['businessIdentifiers', 'businessIdentifiers'],
    ['contactHandles', 'contactHandles'],
    ['contactIds', 'contactUuids'],
    ['tags', 'tags'],
    ['status', 'status'],
    ['bankAccounts', 'bankAccounts'],
  ]

  for (const [source, target] of SIMPLE_FIELDS) {
    if (data[source] !== undefined) {
      requestData[target] = data[source]
    }
  }

  if (data.addresses !== undefined) {
    requestData.addresses = data.addresses.map(addr => ({
      id: addr.id,
      label: addr.label,
      type: addr.type,
      line1: addr.line1,
      line2: addr.line2,
      city: addr.city,
      state: addr.state,
      stateCode: addr.stateCode,
      postalCode: addr.postalCode,
      country: addr.country,
      countryCode: addr.countryCode,
      gstin: addr.gstin,
      companyCode: addr.companyCode,
      isPrimary: addr.isPrimary,
    }))
  }

  return requestData
}

/**
 * Update entity
 */
export async function updateEntity(
  entityId: string,
  data: UpdateEntityRequest
): Promise<ApiResponse<Entity>> {
  // Send camelCase - backend DTOs now use camelCase
  const requestData = buildUpdatePayload(data)
  return patch<Entity>(`/entities/${entityId}`, requestData)
}

/**
 * Delete entity
 */
export async function deleteEntity(
  entityId: string
): Promise<ApiResponse<void>> {
  return del<void>(`/entities/${entityId}`)
}

// ── Workspace organization (the workspace's single shared company entity) ─────

/** Company profile used to seed the shared entity on first create. reseller_id
 * and workspace_id are injected server-side, so the client never sends them. */
export interface WorkspaceOrgPayload {
  name: string
  legalName?: string
  tradeName?: string
  industryType?: string
  website?: string
  logo?: string
  organizationType?: string
  countryCode?: string
  businessIdentifiers?: BusinessIdentifier[]
  contactHandles?: ContactHandle[]
  addresses?: EntityAddress[]
  bankAccounts?: EntityBankAccount[]
}

export interface WorkspaceOrganizationResolution {
  exists: boolean
  entityId: string
  entity: Entity | null
}

export interface WorkspaceOrganizationResult {
  entityId: string
  created: boolean       // false when an existing entity was attached
  entity: Entity | null
}

/**
 * Resolve the workspace's shared company entity (accounts-api is the source of
 * truth). exists=false when the workspace has no company yet. The workspace is
 * taken from the session server-side.
 */
export async function resolveWorkspaceOrganization(): Promise<ApiResponse<WorkspaceOrganizationResolution>> {
  return get<WorkspaceOrganizationResolution>('/workspace/organization')
}

/**
 * Idempotently return — or, on first call for the workspace, create — the
 * shared company entity, seeded from `data`. When an entity already exists it
 * is returned unchanged (created=false); the caller writes through any edits
 * via updateEntity.
 */
export async function createOrAttachWorkspaceOrganization(
  data: WorkspaceOrgPayload
): Promise<ApiResponse<WorkspaceOrganizationResult>> {
  return post<WorkspaceOrganizationResult>('/workspace/organization', data)
}

/**
 * List entities with optional filtering
 */
export async function listEntities(
  filters?: ListEntitiesFilter,
  pageSize?: number,
  pageToken?: string
): Promise<ApiResponse<ListEntitiesResponse>> {
  const query = buildQueryString({
    pageSize,
    pageToken,
    status: filters?.status,
    tags: filters?.tags?.join(','),
    query: filters?.query,
  })

  // accounts-api returns a flat array, so we need to transform it
  const response = await get<Entity[] | ListEntitiesResponse>(`/entities${query}`)

  if (response.error) {
    return response as ApiResponse<ListEntitiesResponse>
  }

  // If the response is already in the correct format, return it
  if (response.data && 'entities' in response.data) {
    return response as ApiResponse<ListEntitiesResponse>
  }

  // Transform array response to ListEntitiesResponse format
  return {
    data: {
      entities: (response.data as Entity[]) ?? [],
    },
  }
}

/**
 * Helper: Extract primary address from entity
 */
export function getPrimaryAddress(entity: Entity): EntityAddress | undefined {
  if (!entity.addresses || entity.addresses.length === 0) {return undefined}
  return entity.addresses.find(addr => addr.isPrimary) ?? entity.addresses[0]
}

/**
 * Helper: Extract primary GSTIN from entity
 * Note: GSTIN is stored in the address object, not in businessIdentifiers
 */
export function getPrimaryGSTIN(entity: Entity): string | undefined {
  const primaryAddress = getPrimaryAddress(entity)
  return primaryAddress?.gstin
}

/**
 * Helper: Format entity display name with identifier
 */
export function formatEntityName(entity: Entity): string {
  const gstin = getPrimaryGSTIN(entity)
  return gstin ? `${entity.name} (${gstin})` : entity.name
}

/**
 * Search for entities by business identifiers
 * Used for duplicate detection before creating new entities
 */
export async function searchByIdentifiers(
  identifiers: {
    gstin?: string
    pan?: string
    cin?: string
    tan?: string
    phone?: string
    email?: string
  }
): Promise<ApiResponse<Entity[]>> {
  return post<Entity[]>('/entities/search-by-identifiers', identifiers)
}

/**
 * Search for entities by name (normalized matching)
 * Matches company names ignoring spaces, special characters, and case
 * Example: "ABC Corp" matches "ABC-Corp", "abc corp", "A.B.C. CORP", etc.
 */
export async function searchByName(
  name: string,
  countryCode?: string
): Promise<ApiResponse<Entity[]>> {
  return post<Entity[]>('/entities/search-by-name', { name, countryCode })
}
